import { useEffect, useMemo, useRef, useState } from 'react';
import { Camera, Check, RefreshCw, X } from 'lucide-react';

interface CameraCaptureModalProps {
  open: boolean;
  loading?: boolean;
  title: string;
  description: string;
  onCancel: () => void;
  onCaptured: (file: File) => Promise<void> | void;
}

export function CameraCaptureModal({
  open,
  loading = false,
  title,
  description,
  onCancel,
  onCaptured,
}: CameraCaptureModalProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  const [capturedFile, setCapturedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submitDisabled = useMemo(() => !capturedFile || loading, [capturedFile, loading]);

  useEffect(() => {
    if (!open) return;
    let mounted = true;

    const startCamera = async () => {
      try {
        setError(null);
        setCapturedFile(null);
        setPreviewUrl(null);

        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user' },
          audio: false,
        });

        if (!mounted) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch (err) {
        setError('No se pudo acceder a la camara. Verifica permisos e intenta de nuevo.');
      }
    };

    startCamera();

    return () => {
      mounted = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, [open]);

  const takePhoto = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const width = video.videoWidth || 720;
    const height = video.videoHeight || 960;

    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, width, height);

    const blob: Blob | null = await new Promise((resolve) => {
      canvas.toBlob((result) => resolve(result), 'image/jpeg', 0.92);
    });

    if (!blob) {
      setError('No se pudo capturar la foto. Intenta nuevamente.');
      return;
    }

    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }
    const file = new File([blob], `worker-camera-${Date.now()}.jpg`, { type: 'image/jpeg' });
    const nextPreview = URL.createObjectURL(file);
    previewUrlRef.current = nextPreview;

    setCapturedFile(file);
    setPreviewUrl(nextPreview);
    setError(null);
  };

  const resetPhoto = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
    setCapturedFile(null);
  };

  const handleSave = async () => {
    if (!capturedFile) return;
    await onCaptured(capturedFile);
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(2, 6, 23, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '680px',
          borderRadius: '20px',
          background: '#0f172a',
          color: '#fff',
          border: '1px solid rgba(255,255,255,0.15)',
          boxShadow: '0 24px 60px rgba(2, 6, 23, 0.55)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '14px 16px',
            borderBottom: '1px solid rgba(255,255,255,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800 }}>{title}</h3>
            <p style={{ margin: '4px 0 0', color: '#cbd5e1', fontSize: '13px' }}>{description}</p>
          </div>
          {/* <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              border: '1px solid rgba(255,255,255,0.18)',
              background: 'transparent',
              color: '#fff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <X size={16} />
          </button> */}
        </div>

        <div style={{ padding: '16px' }}>
          {!previewUrl ? (
            <div
              style={{
                borderRadius: '14px',
                border: '1px solid rgba(255,255,255,0.14)',
                overflow: 'hidden',
                background: '#020617',
              }}
            >
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                style={{ width: '100%', maxHeight: '440px', objectFit: 'cover', display: 'block' }}
              />
            </div>
          ) : (
            <div
              style={{
                borderRadius: '14px',
                border: '1px solid rgba(255,255,255,0.14)',
                overflow: 'hidden',
                background: '#020617',
              }}
            >
              <img
                src={previewUrl}
                alt="Previsualizacion de foto"
                style={{ width: '100%', maxHeight: '440px', objectFit: 'cover', display: 'block' }}
              />
            </div>
          )}

          {error && (
            <p
              style={{
                marginTop: '10px',
                fontSize: '13px',
                color: '#fecaca',
                background: '#7f1d1d',
                border: '1px solid #dc2626',
                borderRadius: '8px',
                padding: '8px 10px',
              }}
            >
              {error}
            </p>
          )}

          <canvas ref={canvasRef} style={{ display: 'none' }} />

          <div style={{ marginTop: '14px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {!previewUrl ? (
              <button
                type="button"
                onClick={takePhoto}
                style={{
                  height: '42px',
                  borderRadius: '10px',
                  border: 'none',
                  background: 'linear-gradient(90deg, #f59e0b, #facc15)',
                  color: '#0f172a',
                  fontWeight: 800,
                  padding: '0 14px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                }}
              >
                <Camera size={16} />
                Tomar foto
              </button>
            ) : (
              <button
                type="button"
                onClick={resetPhoto}
                disabled={loading}
                style={{
                  height: '42px',
                  borderRadius: '10px',
                  border: '1px solid rgba(255,255,255,0.18)',
                  background: 'transparent',
                  color: '#fff',
                  fontWeight: 700,
                  padding: '0 14px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                }}
              >
                <RefreshCw size={16} />
                Repetir foto
              </button>
            )}

            <button
              type="button"
              onClick={handleSave}
              disabled={submitDisabled}
              style={{
                height: '42px',
                borderRadius: '10px',
                border: 'none',
                background: '#22c55e',
                color: '#052e16',
                fontWeight: 800,
                padding: '0 14px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                cursor: submitDisabled ? 'not-allowed' : 'pointer',
                opacity: submitDisabled ? 0.55 : 1,
              }}
            >
              <Check size={16} />
              {loading ? 'Validando...' : 'Usar esta foto'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
